import { HttpErrorResponse } from '@angular/common/http';

export type GoogleSsoStep = 'credentials' | 'twoFactor';

export interface GoogleSsoOutcome {
  nextStep: GoogleSsoStep;
  // Null when the backend is asking for a 2FA code rather than reporting a failure.
  message: string | null;
  keepCredential: boolean;
}

const GENERIC_MESSAGE = 'Something went wrong. Please try again.';

const RETRY_CODE_MESSAGES: Record<string, string> = {
  TWO_FA_CODE_IS_WRONG: 'That code is not right. Check your authenticator and try again.',
  TWO_FA_CODE_IS_INVALID: 'That code is not right. Check your authenticator and try again.',
  TWO_FA_CODE_ERROR: 'That code is not right. Check your authenticator and try again.',
};

const RESTART_MESSAGES: Record<string, string> = {
  GOOGLE_USER_NOT_FOUND: "This Google account isn't registered. Contact an administrator to request access.",
  GOOGLE_SUBJECT_MISMATCH: "This Google account doesn't match the one linked to that user. Contact an administrator.",
  GOOGLE_DOMAIN_NOT_ALLOWED: 'Use your Stillman Google account to sign in.',
  GOOGLE_EMAIL_NOT_VERIFIED: 'This Google account has no verified email address.',
  SSO_DISABLED_FOR_USER: "Google sign-in isn't enabled for this account.",
  USER_IS_INACTIVE: 'This account is inactive. Contact an administrator.',
  GOOGLE_TOKEN_EXPIRED: 'That sign-in expired. Please sign in with Google again.',
  GOOGLE_TOKEN_INVALID: 'Google sign-in failed. Please try again.',
  TOO_MANY_LOGIN_ATTEMPTS: 'Too many attempts. Wait a minute and try again.',
};

export function isTwoFactorRequired(error: HttpErrorResponse): boolean {
  return serverCode(error) === 'TWO_FA_CODE_IS_EMPTY';
}

export function mapGoogleSsoError(error: HttpErrorResponse): GoogleSsoOutcome {
  const code = serverCode(error);

  if (code === 'TWO_FA_CODE_IS_EMPTY') {
    return { nextStep: 'twoFactor', message: null, keepCredential: true };
  }

  if (code && RETRY_CODE_MESSAGES[code]) {
    return { nextStep: 'twoFactor', message: RETRY_CODE_MESSAGES[code], keepCredential: true };
  }

  return {
    nextStep: 'credentials',
    message: (code && RESTART_MESSAGES[code]) || GENERIC_MESSAGE,
    keepCredential: false,
  };
}

function serverCode(error: HttpErrorResponse): string | null {
  const message: unknown = error?.error?.message;

  return typeof message === 'string' && message.length > 0 ? message : null;
}
